<script setup>
/**
 * Критерии оценки жюри: загрузка сохранённых оценок или шаблона списка,
 * валидация ответов API, отправка без alert.
 */
import { useStore } from "vuex";
import { ref, onMounted, computed } from "vue";
import axios from "axios";

const store = useStore();

const isSubmitting = ref(false);
const isSubmitComplete = ref(false);
const infoMessage = ref("");

/** Пока идёт первая загрузка (criteria.php / criteriaList) */
const isInitializing = ref(true);
/** Ошибка начальной загрузки: PARAMS | NETWORK | DATA */
const loadError = ref(null);
/** Ошибка сохранения (save.php) */
const submitError = ref(null);

/** Смещение шкалы (0 vs 1) из VUE_APP_RATING_START */
const changeIndex = ref(0);
const isCriteriaReady = ref(false);
const criteria = ref([]);
const savedCriteria = ref([]);
const firstAction = ref(true);
/** Список критериев для задачи не найден (NOT_FOUND с бэкенда) */
const taskError = ref(false);

const token = computed(() => store.getters["authModule/token"]);

// eslint-disable-next-line no-undef
const props = defineProps({
	teamId: { type: String, required: true },
	userId: { type: String, required: true },
	taskId: { type: String, required: true },
});

/** 0 = общий criteriaList.php, иначе — с taskId (env — строка, сравниваем через Number) */
const useCommonCriteriaList = () =>
	Number(process.env.VUE_APP_CRITERIAS_TYPE) === 0;

onMounted(() => {
	void init();
});

function resetUiState() {
	taskError.value = false;
	loadError.value = null;
	criteria.value = [];
	savedCriteria.value = [];
	isCriteriaReady.value = false;
}

async function init() {
	resetUiState();
	isInitializing.value = true;
	submitError.value = null;

	if (+process.env.VUE_APP_RATING_START === 0) {
		changeIndex.value = 1;
	}

	if (!String(props.teamId || "").trim() || !String(props.userId || "").trim()) {
		loadError.value = "PARAMS";
		isInitializing.value = false;
		return;
	}

	const admin = process.env.VUE_APP_ADMIN_URL + "/api";
	const uid = encodeURIComponent(props.userId);
	const tid = encodeURIComponent(props.teamId);
	const tok = encodeURIComponent(token.value || "");
	const criteriaUrl = `${admin}/criteria.php?userId=${uid}&teamId=${tid}&token=${tok}`;

	try {
		const { data: savedRaw } = await axios.get(criteriaUrl);

		if (!Array.isArray(savedRaw)) {
			console.error("TheCriteria: criteria.php ожидался массив", savedRaw);
			loadError.value = "DATA";
			return;
		}

		savedCriteria.value = savedRaw;

		if (savedRaw.length > 0) {
			firstAction.value = false;
			criteria.value = savedRaw;
			infoMessage.value =
				"Для отправки результатов измените значение выбранного критерия";
			return;
		}

		// Шаблон критериев
		const common = useCommonCriteriaList();
		if (!common && !String(props.taskId || "").trim()) {
			taskError.value = true;
			return;
		}

		let listUrl =
			process.env.VUE_APP_ADMIN_URL + "/api/data/criteriaList.php";
		if (!common) {
			listUrl += `?taskId=${encodeURIComponent(props.taskId)}`;
		}

		const { data: listRaw } = await axios.get(listUrl);

		if (listRaw && typeof listRaw === "object" && !Array.isArray(listRaw)) {
			if (listRaw.error === "NOT_FOUND") {
				taskError.value = true;
				criteria.value = [];
				return;
			}
			console.error("TheCriteria: неожиданный объект списка критериев", listRaw);
			loadError.value = "DATA";
			return;
		}

		if (!Array.isArray(listRaw)) {
			console.error("TheCriteria: criteriaList ожидался массив", listRaw);
			loadError.value = "DATA";
			return;
		}

		criteria.value = listRaw;
		firstAction.value = true;
		infoMessage.value =
			"Для отправки результатов укажите значения всех критериев";
	} catch (e) {
		console.error("TheCriteria init:", e);
		loadError.value = "NETWORK";
		criteria.value = [];
		savedCriteria.value = [];
	} finally {
		isInitializing.value = false;
	}
}

/**
 * @param ratingOffset — то же, что changeIndex (смещение шкалы), имя параметра без конфликта с ref
 */
const setCriteria = (value, criteriaIndex, sectionIndex, ratingOffset) => {
	isCriteriaReady.value = true;

	const item = criteria.value[criteriaIndex];
	if (!item?.sections?.[sectionIndex]) {
		return;
	}
	item.sections[sectionIndex].activeIndex = value - ratingOffset;

	for (const c of criteria.value) {
		for (const section of c.sections || []) {
			if (section.activeIndex === 0 - ratingOffset) {
				isCriteriaReady.value = false;
			}
		}
	}
};

const criteriaReady = () => {
	if (!firstAction.value && !isCriteriaReady.value) {
		isCriteriaReady.value = true;
	}
};

const onSubmit = async () => {
	submitError.value = null;
	isSubmitting.value = true;

	try {
		const headers = {
			Authorization: "Bearer " + token.value,
		};

		const values = {
			criteria: criteria.value,
			teamId: props.teamId,
			userId: props.userId,
		};

		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.log("TheCriteria save payload", values);
		}

		const authSendResults = process.env.VUE_APP_ADMIN_URL + "/api/save.php";
		const { data } = await axios.post(authSendResults, values, { headers });

		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line no-console
			console.log("TheCriteria save response", data);
		}

		if (data && data.status === "SUCCESS") {
			isSubmitComplete.value = true;
			isCriteriaReady.value = false;
			setTimeout(() => {
				isSubmitComplete.value = false;
			}, 3000);
		} else {
			submitError.value = "SAVE_FAILED";
		}
	} catch (e) {
		console.error("TheCriteria onSubmit:", e);
		submitError.value = "NETWORK";
	} finally {
		isSubmitting.value = false;
	}
};
</script>

<template>
	<div v-if="isInitializing" class="d-flex justify-content-center py-5">
		<div class="spinner-border text-primary" role="status"></div>
	</div>

	<div v-else-if="loadError === 'NETWORK'" class="alert alert-danger" role="alert">
		Не удалось загрузить критерии. Проверьте соединение и обновите страницу.
	</div>
	<div v-else-if="loadError === 'PARAMS'" class="alert alert-danger" role="alert">
		Некорректные параметры страницы (команда или пользователь). Вернитесь к списку
		команд.
	</div>
	<div v-else-if="loadError === 'DATA'" class="alert alert-danger" role="alert">
		Ответ сервера имеет неверный формат. Обратитесь к администратору.
	</div>

	<div v-else-if="taskError">
		<div class="alert alert-danger alert-dismissible">
			<h5>Что-то пошло не так...</h5>
			Список критериев для оценки данной команды не найден
		</div>
	</div>

	<div v-else>
		<h3 class="text-center">Критерии оценки</h3>

		<div class="number mt-4">1</div>
		<div class="v-line"></div>

		<div v-for="(item, index) in criteria" :key="index">
			<div class="card card-outline card-info m-0">
				<div class="card-header">
					<h4 class="text-center">{{ item["title"] }}</h4>
				</div>
				<div class="card-body pb-5">
					<div
						v-for="(section, sectionIndex) in item['sections']"
						:key="sectionIndex"
					>
						<div
							:class="[
								'm-auto w-75',
								{ 'border-bottom': item['sections'].length > 1 },
							]"
						>
							<div class="m-4 text-center" v-html="section['name']"></div>
							<div class="d-flex flex-wrap justify-content-center">
								<div
									v-for="value in section['values'] + changeIndex"
									:key="value"
								>
									<div
										:class="[
											'criteria-counter m-3',
											{
												'criteria-counter-selected':
													section['activeIndex'] === value - changeIndex,
											},
										]"
										@click="
											setCriteria(value, index, sectionIndex, changeIndex)
										"
									>
										{{ value - changeIndex }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="card-footer">
					<div class="m-auto w-75 p-3">
						<div class="p-1">Комментарий к оценке раздела</div>
						<textarea
							class="form-control"
							rows="2"
							v-model="item['comment']"
							@keydown="criteriaReady()"
						></textarea>
					</div>
				</div>
			</div>
			<div v-if="index < criteria.length - 1">
				<div class="v-line"></div>
				<div class="number">{{ index + 2 }}</div>
				<div class="v-line"></div>
			</div>
		</div>

		<div class="text-center mt-5 mb-4">
			<div v-if="submitError === 'NETWORK'" class="alert alert-danger">
				Не удалось отправить оценки. Проверьте соединение и попробуйте снова.
			</div>
			<div v-else-if="submitError === 'SAVE_FAILED'" class="alert alert-warning">
				Сервер не подтвердил сохранение. Попробуйте ещё раз или обновите страницу.
			</div>

			<div v-if="isSubmitComplete" class="alert alert-success text-center">
				<v-icon
					class="success-icon--white m-2"
					icon="mdi:check-circle-outline"
				></v-icon>
				<h4>Оценки отправлены</h4>
			</div>

			<button
				class="btn btn-send"
				type="button"
				@click="onSubmit()"
				:disabled="!isCriteriaReady || isSubmitting"
			>
				<svg
					v-if="isSubmitting && !isSubmitComplete"
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
				>
					<g>
						<circle cx="3" cy="12" r="2" fill="#fff" />
						<circle cx="21" cy="12" r="2" fill="#fff" />
						<circle cx="12" cy="21" r="2" fill="#fff" />
						<circle cx="12" cy="3" r="2" fill="#fff" />
						<circle cx="5.64" cy="5.64" r="2" fill="#fff" />
						<circle cx="18.36" cy="18.36" r="2" fill="#fff" />
						<circle cx="5.64" cy="18.36" r="2" fill="#fff" />
						<circle cx="18.36" cy="5.64" r="2" fill="#fff" />
						<animateTransform
							attributeName="transform"
							dur="1.5s"
							repeatCount="indefinite"
							type="rotate"
							values="0 12 12;360 12 12"
						/>
					</g>
				</svg>
				<span v-else>Отправить оценки</span>
			</button>

			<div v-if="!isCriteriaReady" class="m-2 text-danger text-bold">
				{{ infoMessage }}
			</div>
		</div>
	</div>
</template>

<style scoped>
.criteria-counter {
	background-color: #eff3f9;
	padding: 10px 10px;
	margin: 10px;
	font-size: 21px;
	font-weight: bold;
	text-align: center;
	cursor: pointer;
	border-radius: 50px;
	-moz-border-radius: 50px;
	-webkit-border-radius: 50px;
	width: 52px;
	transition: 0.3s;
}

.criteria-counter:hover {
	background-color: #bdd8e8 !important;
}

.criteria-counter-selected {
	background-color: #80b5d3 !important;
	color: #fff;
}
</style>
